import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { clearSession, getCurrentUser, getRefreshToken } from "../services/auth";
import { getProfile, type ProfileUser } from "../services/profile";
import { LogOut, X, Loader2 } from "lucide-react";
import API from "../services/API";

interface ProfilePanelProps {
 open: boolean;
 onClose: () => void;
}

export default function ProfilePanel({ open, onClose }: ProfilePanelProps) {
 const navigate = useNavigate();
 const currentUser = getCurrentUser();
 const [profile, setProfile] = useState<ProfileUser | null>(null);
 const [loading, setLoading] = useState(false);

 useEffect(() => {
 if (!open) return;

 let cancelled = false;
 setLoading(true);
 getProfile()
 .then((nextProfile) => {
 if (!cancelled) setProfile(nextProfile);
 })
 .catch(() => {
 if (!cancelled) setProfile(null);
 })
 .finally(() => {
 if (!cancelled) setLoading(false);
 });

 return () => {
 cancelled = true;
 };
 }, [open]);



 const handleSignOut = async () => {
 const refreshToken = getRefreshToken();
 try {
 await API.post("/api/auth/logout", { refreshToken });
 } catch {
 // Best-effort — even if the server call fails, we still clear locally
 } finally {
 clearSession();
 onClose();
 navigate("/");
 }
 };



 if (!open) return null;

 const displayName = profile?.full_name || currentUser?.name ||"User";
 const displayEmail = profile?.email || currentUser?.email ||"";
 const employeeId = profile?.id || currentUser?.id;
 const employeeCode = profile?.employee_code;
 const role = profile?.designation || profile?.role || currentUser?.role ||"";
 const initials = displayName.split("").map((n: string) => n[0]).join("").slice(0, 2).toUpperCase();

 return (
 <>
 {/* Backdrop */}
 <div className="fixed inset-0 z-50 bg-black/20 animate-backdrop-in"onClick={onClose} />

 {/* Panel sliding from right */}
 <div className="fixed inset-y-0 right-0 z-50 w-80 max-w-[90vw] flex flex-col animate-slide-in-right bg-white shadow-2xl">
 {/* Header */}
 <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
 <button
 type="button"onClick={handleSignOut}
 className="inline-flex items-center gap-2 text-sm font-medium text-rose-600 transition hover:text-rose-700">
 <LogOut className="h-4 w-4"/>
 Sign Out
 </button>
 <button
 type="button"onClick={onClose}
 aria-label="Close"className="rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600">
 <X className="h-5 w-5"/>
 </button>
 </div>

 {/* Scrollable content */}
 <div className="flex-1 overflow-y-auto">
 {loading ? (
 <div className="flex h-48 items-center justify-center">
 <Loader2 className="h-6 w-6 animate-spin text-slate-400"/>
 </div>
 ) : (
 <>
 {/* Profile Card */}
 <div className="flex flex-col items-center px-5 py-6">
 {/* Avatar with initials */}
 <div className="flex h-18 w-18 items-center justify-center rounded-full bg-[var(--color-primary)] text-xl font-bold text-white shadow-lg"style={{ height: "72px", width: "72px"}}>
 {initials}
 </div>

 <h3 className="mt-3 text-lg font-bold text-slate-900">
 {displayName}
 </h3>
 <p className="mt-0.5 text-sm text-slate-500">{displayEmail}</p>

 {/* Role badge */}
 <span className="mt-2 inline-flex rounded-full bg-slate-100 px-3 py-0.5 text-[11px] font-semibold text-slate-600">
 {role}
 </span>

 {/* Meta info */}
 <div className="mt-3 flex items-center gap-4 text-[11px] text-slate-400">
 {employeeCode && <span>Code: <span className="font-medium text-slate-600">{employeeCode}</span></span>}
 {employeeId && <span>ID: <span className="font-medium text-slate-600">{employeeId}</span></span>}
 </div>
 </div>




 </>
 )}
 </div>
 </div>
 </>
 );
}
